<template>
  <article class="event-passes" v-if="event">
    <h3 class="event-passes__header">
      {{ $t("event.passes.title") }}

      <icon name="event-passes-header" class="event-passes__header-icon" />
    </h3>

    <div class="event-passes__count" v-if="!isAllDeadlinesPassesReached">
      {{
        $t("event.passes.availableAmount", { amount: availablePassesAmount })
      }}
    </div>

    <!-- Не позднее <дедлайн оформления пропуска> -->
    <Notification
      :warning="diffInSeconds(closestDeadline) <= 60 * 60 * 24 * 10"
      v-if="!isAllDeadlinesPassesReached"
    >
      {{
        $t("event.passes.untilDeadline", {
          date: closestDeadline.format("DD MMMM YYYY")
        })
      }}
    </Notification>

    <!-- Закончился срок возможности офрормления пропуска, и можно только скачать -->
    <Notification icon="blank-doc" v-if="isAllDeadlinesPassesReached">
      {{ $t("event.passes.downloadNotification") }}
    </Notification>

    <div class="event-passes__content">
      <table class="event-passes__table">
        <tr v-if="isShowPersonalPasses">
          <td>{{ $t("event.passes.participantPasses") }}</td>
          <td style="position: relative;">
            <span
              v-if="
                !Number(
                  getLocaleValue(
                    this.exhibition,
                    'create_personal_pass_disabled'
                  )
                )
              "
            >
              {{
                $t("event.passes.outOf", {
                  amount: participantPassesAmount,
                  total: availablePersonalPassesAmount
                })
              }}
            </span>
          </td>
          <td
            :class="{
              'event-passes__table-col-dot':
                diffInSeconds(passDeadline.certificate) >= 0 &&
                diffInSeconds(passDeadline.certificate) <= 60 * 60 * 24 * 10
            }"
          >
            <a
              href="#"
              @click.prevent="$modal.show('event-modal-participant-passes')"
              style="display: flex"
            >
              <template
                v-if="
                  hasAccessToWrite &&
                    diffInSeconds(passDeadline.endCertificatePrint) >= 0
                "
              >
                <Tooltip :label="$t('event.passes.makePass')">
                  <icon
                    name="link-icon-edit"
                    v-if="diffInSeconds(passDeadline.certificate) >= 0"
                  />
                </Tooltip>
                <Tooltip :label="$t('event.passes.downloadTooltip')">
                  <icon
                    name="download"
                    v-if="
                      diffInSeconds(passDeadline.startCertificatePrint) < 0 &&
                        diffInSeconds(passDeadline.endCertificatePrint) >= 0
                    "
                  />
                </Tooltip>
              </template>

              <template v-else>
                <Tooltip :label="$t('common.eventCardLook')">
                  <icon class="look" name="look" />
                </Tooltip>
              </template>
            </a>
          </td>
        </tr>
        <tr v-if="isShowAutoPasses">
          <td>{{ $t("event.passes.carPasses") }}</td>
          <td>
            <span
              v-if="
                !Number(
                  getLocaleValue(this.exhibition, 'create_car_pass_disabled')
                )
              "
            >
              {{ autoPassesAmount }}</span
            >
          </td>
          <td>
            <a
              style="display: flex"
              href="#"
              @click.prevent="$modal.show('event-modal-auto-passes')"
            >
              <template
                v-if="
                  hasAccessToWrite &&
                    !Number(
                      getLocaleValue(
                        this.exhibition,
                        'create_car_pass_disabled'
                      )
                    )
                "
              >
                <Tooltip
                  :label="$t('event.passes.makePass')"
                  v-if="diffInSeconds(passDeadline.car) >= 0"
                >
                  <icon name="link-icon-edit" />
                </Tooltip>

                <Tooltip :label="$t('event.passes.downloadTooltip')" v-else>
                  <icon name="download" />
                </Tooltip>
              </template>

              <template v-else>
                <Tooltip :label="$t('common.eventCardLook')">
                  <icon class="look" name="look" />
                </Tooltip>
              </template>
            </a>
          </td>
        </tr>
        <tr v-if="isShowInstallationPass">
          <td>{{ $t("event.passes.mountingPasses") }}</td>
          <td>
            <span
              v-if="
                !Number(
                  getLocaleValue(
                    this.exhibition,
                    'create_install_pass_disabled'
                  )
                )
              "
            >
              {{
                $t("event.passes.outOf", {
                  amount: montagePassesAmount,
                  total: event.quota?.installation || 0
                })
              }}</span
            >
          </td>
          <td
            :class="{
              'event-passes__table-col-dot':
                diffInSeconds(passDeadline.installation) >= 0 &&
                diffInSeconds(passDeadline.installation) <= 60 * 60 * 24 * 10
            }"
          >
            <a
              href="#"
              @click.prevent="$modal.show('event-modal-montage-passes')"
              style="display: flex"
            >
              <template
                v-if="
                  hasAccessToWrite &&
                    diffInSeconds(passDeadline.endInstallationPrint) >= 0 &&
                    !Number(
                      getLocaleValue(
                        this.exhibition,
                        'create_install_pass_disabled'
                      )
                    )
                "
              >
                <Tooltip
                  :label="$t('event.passes.makePass')"
                  v-if="diffInSeconds(passDeadline.installation) >= 0"
                >
                  <icon name="link-icon-edit" />
                </Tooltip>

                <Tooltip
                  v-if="
                    diffInSeconds(passDeadline.startInstallationPrint) < 0 &&
                      diffInSeconds(passDeadline.endInstallationPrint) >= 0 &&
                      !Number(
                        getLocaleValue(
                          this.exhibition,
                          'create_install_pass_disabled'
                        )
                      )
                  "
                  :label="$t('event.passes.downloadTooltip')"
                >
                  <icon name="download" />
                </Tooltip>
              </template>

              <template v-else>
                <Tooltip :label="$t('common.eventCardLook')">
                  <icon class="look" name="look" />
                </Tooltip>
              </template>
            </a>
          </td>
        </tr>
      </table>
    </div>

    <!-- Отображается, если колл. организатор -->
    <div class="event-passes__footer" v-if="isEventOrganizer">
      <div class="event-passes__footer-text">
        <icon name="info-grey" />
        <p style="margin: 0">
          {{ $t("event.passes.subexpPart1") }}

          <a
            href="#"
            @click.prevent="$modal.show('event-modal-subexponent')"
            class="event-passes__footer-text-link"
          >
            {{ $t("event.passes.subexpPart2") }}&nbsp;
          </a>
        </p>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState, mapGetters } from "vuex";

import diffInSeconds from "@/helpers/diffInSeconds";
import passHelper from "@/helpers/pass";
import moment from "moment/moment";
import { i18n } from "@v2/shared/config";
import { Tooltip, Notification } from "@v2/shared/ui/components";
moment.locale(i18n.locale);
import { getLocaleValue } from "@/helpers/common";

export default {
  name: "EventPasses",

  components: {
    Notification,
    Tooltip
  },
  props: {
    eventId: {
      type: Number,
      required: true
    }
  },
  computed: {
    ...mapState("event", ["event"]),
    ...mapState("passes", [
      "participantPasses",
      "participantPassesAmount",
      "autoPasses",
      "autoPassesAmount",
      "montagePasses",
      "montagePassesAmount"
    ]),
    ...mapGetters("event", [
      "isEventOrganizer",
      "isEventMontage",
      "hasAccessToWrite",
      "passDeadline",
      "subParticipantAviableQuotas",
      "isOrganizerAndMedia",
      "isPartTime",
      "isSpecialProject",
      "exhibition"
    ]),

    /**
     * Возвращает ближайших из дедлайнов на пропуска.
     */
    closestDeadline() {
      if (this.event.type === "installer")
        return this.passDeadline.installation.locale(i18n.locale);
      if (this.event.type !== "installer" && this.event.is_self_builder)
        return passHelper
          .closestDeadline(this.passDeadline)
          .locale(i18n.locale);

      return this.passDeadline.certificate;
    },
    isShowPersonalPasses() {
      return !this.isEventMontage;
    },
    isShowAutoPasses() {
      return !this.isOrganizerAndMedia;
    },
    isShowInstallationPass() {
      return (
        ((this.isEventMontage || !!this.event.is_self_builder) &&
          !this.isSpecialProject &&
          !this.isOrganizerAndMedia) ||
        Number(getLocaleValue(this.exhibition, "create_install_pass_disabled"))
      );
    },
    isAllDeadlinesPassesReached() {
      return passHelper.isAllDeadlinesPassesReached(this.passDeadline);
    },

    availablePassesAmount() {
      if (!this.event.quota) return 0;

      return Math.max(
        this.event.quota.certificate +
          this.event.quota.installation -
          this.participantPassesAmount -
          this.subParticipantAviableQuotas -
          this.montagePassesAmount
      );
    },

    availablePersonalPassesAmount() {
      if (!this.event.quota) return 0;

      return Math.max(
        this.event.quota.certificate - this.subParticipantAviableQuotas
      );
    }
  },
  methods: {
    diffInSeconds,
    getLocaleValue
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-passes {
  width: 460px;

  @include respond-down(lg) {
    width: 350px;
  }

  margin-top: 20px;

  box-sizing: border-box;

  position: relative;

  display: flex;
  flex-direction: column;

  @include respond-down(md) {
    margin-top: 0;
  }
  &.services {
    margin-top: 30px;
  }
  &.event-organizer-and-media,
  &.event-special-project {
    margin-top: 0;
  }
  &__header {
    @include h2();

    padding: 20px 30px;

    @include respond-down(md) {
      padding: 15px 15px 34px 15px;
      font-size: 1rem;
    }

    border-bottom: 1px solid var(--gray-darker);

    position: relative;

    &-icon {
      position: absolute;
      right: 19px;
      bottom: 0;

      width: 66px;
      height: 63px;

      @include respond-down(lg) {
        width: 56px;
        height: 53px;
      }
    }
  }

  &__count {
    position: absolute;
    left: 160px;
    top: 26px;

    font-size: 12px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 18px;
    color: #a3a8b3;

    @include respond-down(lg) {
      left: 140px;
      top: 30px;
    }

    @include respond-down(md) {
      left: 15px;
      top: 45px;
    }
  }

  &__content {
    padding: 30px;

    @include respond-down(md) {
      padding: 15px;
    }
    & .svg-icon.svg-icon--look {
      width: 20px;
      height: 20px;
    }
  }

  &__table {
    font-size: 13px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 20px;
    letter-spacing: normal;
    color: #000000;
    width: 100%;

    @include respond-down(md) {
      font-size: 12px;
      line-height: 1.67;
    }

    td {
      padding-top: 14px;
      padding-bottom: 13px;
    }

    tr:not(:last-child) td {
      border-bottom: 1px solid rgba(205, 206, 208, 0.5);
    }

    td:nth-child(1) {
      width: 240px;
      padding-right: 14px;

      @include respond-down(lg) {
        width: 130px;
      }
    }

    td:nth-child(2) {
      width: 70px;
      padding-right: 15px;

      @include respond-down(lg) {
        width: 60px;
      }

      color: #6d7179;
    }

    td:nth-child(3) {
      width: 53px;
    }

    .svg-icon {
      margin-right: 5px;
      width: 16px;
      height: 16px;
      color: #009ee3;
    }

    &-col-dot {
      position: relative;

      &::after {
        content: "";
        display: block;

        width: 10px;
        height: 10px;

        background-color: #f6931d;

        border-radius: 50%;

        position: absolute;
        right: 1px;
        top: 50%;

        transform: translateY(-50%);

        @include respond-down(md) {
          right: 0;
        }
      }
    }
    &-btn {
      @include button-clean();
      margin-left: 7px;
      vertical-align: middle;
    }

    &-text {
      width: 396px;
      display: none;

      box-sizing: border-box;

      border-radius: 1px;
      background-color: #ffffff;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.06);
      border: 1px solid #f1f1f1;

      font-size: 12px;
      color: #6d7179;

      padding: 12px 0 15px 25px;

      position: absolute;

      top: -56px;
      left: -233px;

      .svg-icon {
        position: absolute;
        bottom: -12px;
        right: 93.5px;
      }
    }

    &-btn:hover + &-text {
      display: block;
    }
  }

  &__note {
    font-size: 12px;
    line-height: 1.5;
    color: #a3a8b3;

    padding: 10px 38px 15px 38px;
    margin-top: auto;
  }

  &__footer {
    padding: 23px 0 24px 40px;
    margin-top: auto;

    border-top: 1px solid rgba(205, 206, 208, 0.5);

    &-text {
      @include description();

      display: flex;
      align-items: center;

      &-link {
        @include hyperlink();
      }

      .svg-icon {
        width: 16px;
        height: 16px;
        margin-right: 8px;
      }
    }
  }
}
</style>
